/**
 * Finalized Chart View Page
 * ==========================
 * Read-only view for finalized periodontal charts.
 * Shows all chart information without any editable fields.
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Calendar,
  User,
  Mail,
  Phone,
  FileText,
  FileDown,
  CheckCircle,
  Activity,
  AlertCircle,
  ChevronDown,
} from 'lucide-react';
import toast from 'react-hot-toast';

import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { formatDate, formatDateTime } from '@/lib/utils';
import { getPatient } from '@/api/patients';
import { useAuthStore } from '@/stores/authStore';
import type { Chart, Patient, ToothData } from '@/types';
import { useQuery } from '@tanstack/react-query';

// Import tooth images
import tooth1 from '@/assets/img/tooth-01.png';
import tooth2 from '@/assets/img/tooth-02.png';
import tooth3 from '@/assets/img/tooth-03.png';
import tooth4 from '@/assets/img/tooth-04.png';
import tooth5 from '@/assets/img/tooth-05.png';
import tooth6 from '@/assets/img/tooth-06.png';
import tooth7 from '@/assets/img/tooth-07.png';
import tooth8 from '@/assets/img/tooth-08.png';
import tooth9 from '@/assets/img/tooth-09.png';
import tooth10 from '@/assets/img/tooth-10.png';
import tooth11 from '@/assets/img/tooth-11.png';
import tooth12 from '@/assets/img/tooth-12.png';
import tooth13 from '@/assets/img/tooth-13.png';
import tooth14 from '@/assets/img/tooth-14.png';
import tooth15 from '@/assets/img/tooth-15.png';
import tooth16 from '@/assets/img/tooth-16.png';
import tooth17 from '@/assets/img/tooth-17.png';
import tooth18 from '@/assets/img/tooth-18.png';
import tooth19 from '@/assets/img/tooth-19.png';
import tooth20 from '@/assets/img/tooth-20.png';
import tooth21 from '@/assets/img/tooth-21.png';
import tooth22 from '@/assets/img/tooth-22.png';
import tooth23 from '@/assets/img/tooth-23.png';
import tooth24 from '@/assets/img/tooth-24.png';
import tooth25 from '@/assets/img/tooth-25.png';
import tooth26 from '@/assets/img/tooth-26.png';
import tooth27 from '@/assets/img/tooth-27.png';
import tooth28 from '@/assets/img/tooth-28.png';
import tooth29 from '@/assets/img/tooth-29.png';
import tooth30 from '@/assets/img/tooth-30.png';
import tooth31 from '@/assets/img/tooth-31.png';
import tooth32 from '@/assets/img/tooth-32.png';

// Map tooth numbers to their images
const TOOTH_IMAGES: Record<number, string> = {
  1: tooth1, 2: tooth2, 3: tooth3, 4: tooth4, 5: tooth5, 6: tooth6, 7: tooth7, 8: tooth8,
  9: tooth9, 10: tooth10, 11: tooth11, 12: tooth12, 13: tooth13, 14: tooth14, 15: tooth15, 16: tooth16,
  17: tooth17, 18: tooth18, 19: tooth19, 20: tooth20, 21: tooth21, 22: tooth22, 23: tooth23, 24: tooth24,
  25: tooth25, 26: tooth26, 27: tooth27, 28: tooth28, 29: tooth29, 30: tooth30, 31: tooth31, 32: tooth32,
};

interface FinalizedChartViewProps {
  chart: Chart;
}

export default function FinalizedChartView({ chart }: FinalizedChartViewProps) {
  const navigate = useNavigate();
  const [isExporting, setIsExporting] = useState(false);
  const { accessToken } = useAuthStore();

  // Fetch patient data
  const { data: patient } = useQuery<Patient>({
    queryKey: ['patient', chart.patient_id],
    queryFn: () => getPatient(chart.patient_id),
    enabled: !!chart.patient_id,
  });

  const handleExportPDF = async () => {
    if (!patient) {
      toast.error('Patient data not loaded');
      return;
    }

    if (!accessToken) {
      toast.error('Authentication token not found. Please log in again.');
      return;
    }

    try {
      setIsExporting(true);
      
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/api/v1/charts/${chart.id}/export/pdf`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to export PDF');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `PerioChart_${patient?.full_name?.replace(/\s+/g, '_')}_${formatDate(chart.exam_date)}.pdf`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="([^"]+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('PDF exported successfully');
    } catch (err) {
      console.error('Error exporting PDF:', err);
      toast.error('Failed to export PDF');
    } finally {
      setIsExporting(false);
    }
  };

  // Calculate summary statistics
  const getSummaryStats = () => {
    if (!chart.tooth_data || chart.tooth_data.length === 0) {
      return {
        teethPresent: 0,
        teethMissing: 0,
        teethWithImplants: 0,
        totalBOP: 0,
        totalSites: 0,
        avgPocketDepth: 0,
        sites4mmPlus: 0,
        sites6mmPlus: 0,
      };
    }

    let teethPresent = 0;
    let teethMissing = 0;
    let teethWithImplants = 0;
    let totalBOP = 0;
    let totalSites = 0;
    let totalPocketDepth = 0;
    let sites4mmPlus = 0;
    let sites6mmPlus = 0;

    chart.tooth_data.forEach((tooth: ToothData) => {
      if (tooth.status === 'missing') {
        teethMissing++;
      } else if (tooth.status === 'implant') {
        teethWithImplants++;
        teethPresent++;
      } else {
        teethPresent++;
      }

      if (tooth.sites) {
        tooth.sites.forEach((site) => {
          totalSites++;
          if (site.bleeding) totalBOP++;
          if (site.pocket_depth) {
            totalPocketDepth += site.pocket_depth;
            if (site.pocket_depth >= 4) sites4mmPlus++;
            if (site.pocket_depth >= 6) sites6mmPlus++;
          }
        });
      }
    });

    return {
      teethPresent,
      teethMissing,
      teethWithImplants,
      totalBOP,
      totalSites,
      bopPercentage: totalSites > 0 ? Math.round((totalBOP / totalSites) * 100) : 0,
      avgPocketDepth: totalSites > 0 ? (totalPocketDepth / totalSites).toFixed(1) : '0.0',
      sites4mmPlus,
      sites6mmPlus,
    };
  };

  const stats = getSummaryStats();

  // Get tooth status summary
  const getToothStatusSummary = () => {
    if (!chart.tooth_data || chart.tooth_data.length === 0) {
      return {
        present: [],
        missing: [],
        implants: [],
        mobility: [],
        furcation: [],
        restorative: [],
      };
    }

    const summary = {
      present: [] as number[],
      missing: [] as number[],
      implants: [] as number[],
      mobility: [] as { tooth: number; grade: number }[],
      furcation: [] as { tooth: number; grade: number }[],
      restorative: [] as { tooth: number; type: string }[],
    };

    chart.tooth_data.forEach((tooth: ToothData) => {
      if (tooth.status === 'missing') {
        summary.missing.push(tooth.tooth_number);
      } else if (tooth.status === 'implant') {
        summary.implants.push(tooth.tooth_number);
      } else {
        summary.present.push(tooth.tooth_number);
      }

      if (tooth.mobility && tooth.mobility > 0) {
        summary.mobility.push({ tooth: tooth.tooth_number, grade: tooth.mobility });
      }

      if (tooth.furcation && tooth.furcation > 0) {
        summary.furcation.push({ tooth: tooth.tooth_number, grade: tooth.furcation });
      }

      // Check if tooth has any restorative work
      if (tooth.restorative) {
        const hasRestorative = 
          tooth.restorative.crown?.present ||
          (tooth.restorative.fillings && tooth.restorative.fillings.length > 0) ||
          tooth.restorative.root_canal?.treated ||
          tooth.restorative.caries?.present;
        
        if (hasRestorative) {
          summary.restorative.push({
            tooth: tooth.tooth_number,
            type: tooth.restorative.crown?.present ? 'crown' : 'other',
          });
        }
      }
    });

    return summary;
  };

  const toothStatus = getToothStatusSummary();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(-1)}
                className="text-gray-400 hover:text-gray-600"
              >
                <ArrowLeft className="w-6 h-6" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Periodontal Chart</h1>
                <div className="flex items-center gap-3 text-sm text-gray-500 mt-1">
                  <span>Version {chart.version}</span>
                  {chart.is_baseline && <Badge variant="secondary">Baseline</Badge>}
                  <Badge variant="success">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Finalized
                  </Badge>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button
                onClick={handleExportPDF}
                disabled={isExporting || !patient}
                variant="outline"
              >
                <FileDown className="w-4 h-4 mr-2" />
                Export PDF
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        {/* Patient Information */}
        {patient && (
          <Card>
            <div className="p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <User className="h-5 w-5 mr-2 text-blue-600" />
                Patient Information
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div>
                  <p className="text-sm text-gray-500">Name</p>
                  <p className="font-medium text-gray-900">{patient.full_name}</p>
                </div>
                {patient.chart_number && (
                  <div>
                    <p className="text-sm text-gray-500">Chart Number</p>
                    <p className="font-medium text-gray-900">{patient.chart_number}</p>
                  </div>
                )}
                {patient.date_of_birth && (
                  <div>
                    <p className="text-sm text-gray-500">Date of Birth</p>
                    <p className="font-medium text-gray-900">
                      {formatDate(patient.date_of_birth)}
                      {patient.age && ` (${patient.age} years)`}
                    </p>
                  </div>
                )}
                {patient.gender && (
                  <div>
                    <p className="text-sm text-gray-500">Gender</p>
                    <p className="font-medium text-gray-900 capitalize">{patient.gender}</p>
                  </div>
                )}
                {patient.email && (
                  <div>
                    <p className="text-sm text-gray-500 flex items-center">
                      <Mail className="h-4 w-4 mr-1" />
                      Email
                    </p>
                    <p className="font-medium text-gray-900">{patient.email}</p>
                  </div>
                )}
                {patient.phone_primary && (
                  <div>
                    <p className="text-sm text-gray-500 flex items-center">
                      <Phone className="h-4 w-4 mr-1" />
                      Phone
                    </p>
                    <p className="font-medium text-gray-900">{patient.mobile_code ? `+${patient.mobile_code} ` : ''}{patient.phone_primary}</p>
                  </div>
                )}
              </div>
            </div>
          </Card>
        )}

        {/* Chart Information */}
        <Card>
          <div className="p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Calendar className="h-5 w-5 mr-2 text-blue-600" />
              Chart Details
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div>
                <p className="text-sm text-gray-500">Exam Date</p>
                <p className="font-medium text-gray-900">{formatDate(chart.exam_date)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Exam Type</p>
                <p className="font-medium text-gray-900 capitalize">
                  {chart.exam_type?.replace('_', ' ') || 'Periodontal Exam'}
                </p>
              </div>
              {chart.provider_name && (
                <div>
                  <p className="text-sm text-gray-500">Provider</p>
                  <p className="font-medium text-gray-900">{chart.provider_name}</p>
                </div>
              )}
              {chart.hygienist_name && (
                <div>
                  <p className="text-sm text-gray-500">Hygienist</p>
                  <p className="font-medium text-gray-900">{chart.hygienist_name}</p>
                </div>
              )}
              {chart.finalized_at && (
                <div>
                  <p className="text-sm text-gray-500">Finalized On</p>
                  <p className="font-medium text-gray-900">{formatDateTime(chart.finalized_at)}</p>
                </div>
              )}
              {chart.finalized_by && (
                <div>
                  <p className="text-sm text-gray-500">Finalized By</p>
                  <p className="font-medium text-gray-900">{chart.finalized_by}</p>
                </div>
              )}
            </div>
          </div>
        </Card>

        {/* Tooth Status Summary */}
        <Card>
          <div className="p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Activity className="h-5 w-5 mr-2 text-blue-600" />
              Tooth Status Summary
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div>
                <p className="text-sm text-gray-500 mb-2">Teeth Present</p>
                <p className="text-2xl font-bold text-green-600">{stats.teethPresent}</p>
              </div>
              {stats.teethMissing > 0 && (
                <div>
                  <p className="text-sm text-gray-500 mb-2">Missing Teeth</p>
                  <p className="text-2xl font-bold text-gray-600">{stats.teethMissing}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    #{toothStatus.missing.join(', #')}
                  </p>
                </div>
              )}
              {stats.teethWithImplants > 0 && (
                <div>
                  <p className="text-sm text-gray-500 mb-2">Implants</p>
                  <p className="text-2xl font-bold text-blue-600">{stats.teethWithImplants}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    #{toothStatus.implants.join(', #')}
                  </p>
                </div>
              )}
              {toothStatus.mobility.length > 0 && (
                <div>
                  <p className="text-sm text-gray-500 mb-2">Mobility</p>
                  <p className="text-sm text-gray-900">
                    {toothStatus.mobility.map((m) => `#${m.tooth} (Grade ${m.grade})`).join(', ')}
                  </p>
                </div>
              )}
              {toothStatus.furcation.length > 0 && (
                <div>
                  <p className="text-sm text-gray-500 mb-2">Furcation Involvement</p>
                  <p className="text-sm text-gray-900">
                    {toothStatus.furcation.map((f) => `#${f.tooth} (Grade ${f.grade})`).join(', ')}
                  </p>
                </div>
              )}
              {toothStatus.restorative.length > 0 && (
                <div>
                  <p className="text-sm text-gray-500 mb-2">Restorative Work</p>
                  <p className="text-sm text-gray-900">
                    {toothStatus.restorative
                      .map((r) => `#${r.tooth} (${r.type.replace('_', ' ').toUpperCase()})`)
                      .join(', ')}
                  </p>
                </div>
              )}
            </div>
          </div>
        </Card>

        {/* Periodontal Summary */}
        <Card>
          <div className="p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Periodontal Summary</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-6">
              <div>
                <p className="text-sm text-gray-500 mb-1">BOP %</p>
                <p
                  className={`text-2xl font-bold ${
                    (stats.bopPercentage ?? 0) > 30
                      ? 'text-red-600'
                      : (stats.bopPercentage ?? 0) > 15
                      ? 'text-orange-500'
                      : 'text-green-600'
                  }`}
                >
                  {stats.bopPercentage ?? 0}%
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {stats.totalBOP} of {stats.totalSites} sites
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Avg Pocket Depth</p>
                <p
                  className={`text-2xl font-bold ${
                    Number(stats.avgPocketDepth) > 4
                      ? 'text-red-600'
                      : Number(stats.avgPocketDepth) > 3
                      ? 'text-orange-500'
                      : 'text-green-600'
                  }`}
                >
                  {stats.avgPocketDepth}mm
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Sites ≥4mm</p>
                <p
                  className={`text-2xl font-bold ${
                    stats.sites4mmPlus > 20 ? 'text-orange-500' : 'text-gray-900'
                  }`}
                >
                  {stats.sites4mmPlus}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Sites ≥6mm</p>
                <p className={`text-2xl font-bold ${stats.sites6mmPlus > 0 ? 'text-red-600' : 'text-gray-900'}`}>
                  {stats.sites6mmPlus}
                </p>
              </div>
            </div>
          </div>
        </Card>

        {/* Diagnosis */}
        {(chart.aap_stage || chart.aap_grade || chart.aap_extent || chart.risk_level) && (
          <Card>
            <div className="p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <AlertCircle className="h-5 w-5 mr-2 text-blue-600" />
                Diagnosis
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {chart.aap_stage && (
                  <div>
                    <p className="text-sm text-gray-500">AAP Stage</p>
                    <p className="font-medium text-gray-900">{chart.aap_stage}</p>
                  </div>
                )}
                {chart.aap_grade && (
                  <div>
                    <p className="text-sm text-gray-500">AAP Grade</p>
                    <p className="font-medium text-gray-900">{chart.aap_grade}</p>
                  </div>
                )}
                {chart.aap_extent && (
                  <div>
                    <p className="text-sm text-gray-500">AAP Extent</p>
                    <p className="font-medium text-gray-900">{chart.aap_extent}</p>
                  </div>
                )}
                {chart.risk_level && (
                  <div>
                    <p className="text-sm text-gray-500">Risk Level</p>
                    <p
                      className={`font-semibold ${
                        chart.risk_level === 'high'
                          ? 'text-red-600'
                          : chart.risk_level === 'moderate'
                          ? 'text-orange-600'
                          : 'text-green-600'
                      }`}
                    >
                      {chart.risk_level.toUpperCase()}
                    </p>
                  </div>
                )}
              </div>
              {chart.risk_factors && chart.risk_factors.length > 0 && (
                <div className="mt-4">
                  <p className="text-sm text-gray-500 mb-2">Risk Factors</p>
                  <div className="flex flex-wrap gap-2">
                    {chart.risk_factors.map((factor, index) => (
                      <Badge key={index} variant="secondary">
                        {factor}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              {chart.referral_recommended && (
                <div className="mt-4 p-3 bg-orange-50 border border-orange-200 rounded-md">
                  <p className="text-sm font-medium text-orange-800">
                    ⚠️ Referral to Periodontist Recommended
                  </p>
                  {chart.referral_reason && (
                    <p className="text-sm text-orange-700 mt-1">{chart.referral_reason}</p>
                  )}
                </div>
              )}
            </div>
          </Card>
        )}

        {/* Detailed Tooth Measurements Table */}
        {chart.tooth_data && chart.tooth_data.length > 0 && (
          <Card>
            <div className="p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <FileText className="h-5 w-5 mr-2 text-blue-600" />
                Comprehensive Tooth Chart
              </h2>
              <ToothMeasurementsTable teeth={chart.tooth_data} />
            </div>
          </Card>
        )}

        {/* Detailed Tooth Summary */}
        {chart.tooth_data && chart.tooth_data.length > 0 && (
          <Card>
            <div className="p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <FileText className="h-5 w-5 mr-2 text-blue-600" />
                Detailed Tooth Summary
              </h2>
              <DetailedToothSummary teeth={chart.tooth_data} />
            </div>
          </Card>
        )}

        {/* Clinical Notes */}
        {chart.clinical_notes && (
          <Card>
            <div className="p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <FileText className="h-5 w-5 mr-2 text-blue-600" />
                Clinical Notes
              </h2>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-gray-900 whitespace-pre-wrap">{chart.clinical_notes}</p>
              </div>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}

// Tooth Measurements Table Component - Horizontal Format (like PDF)
function ToothMeasurementsTable({ teeth }: { teeth: ToothData[] }) {
  // Create lookup map for teeth
  const toothMap = new Map(teeth.map((t) => [t.tooth_number, t]));

  const getToothData = (toothNum: number) => toothMap.get(toothNum);

  const getSiteMeasurements = (tooth: ToothData | undefined, siteNames: string[]) => {
    if (!tooth || !tooth.sites) return [];
    return tooth.sites
      .filter((s) => siteNames.includes(s.site))
      .sort((a, b) => siteNames.indexOf(a.site) - siteNames.indexOf(b.site))
      .map((s) => s.pocket_depth)
      .filter((pd) => pd !== null);
  };

  const hasBleedingOnProbing = (tooth: ToothData | undefined) => {
    return tooth?.sites?.some((s) => s.bleeding) || false;
  };

  const getCrownInfo = (tooth: ToothData | undefined) => {
    if (!tooth?.restorative?.crown?.present) return null;
    const crown = tooth.restorative.crown;
    const materialMap: Record<string, string> = {
      pfm: 'PFM',
      ceramic: 'CER',
      zirconia: 'ZIR',
      gold: 'AU',
      emax: 'EMX',
      stainless_steel: 'SS',
    };
    const conditionMap: Record<string, string> = {
      good: 'G',
      fair: 'F',
      poor: 'P',
    };
    return {
      material: crown.material ? materialMap[crown.material] || 'OTH' : '',
      condition: crown.condition ? conditionMap[crown.condition] || '' : '',
    };
  };

  const getFillings = (tooth: ToothData | undefined) => {
    if (!tooth?.restorative?.fillings) return [];
    return tooth.restorative.fillings.flatMap((f) => f.surfaces);
  };

  const getCaries = (tooth: ToothData | undefined) => {
    if (!tooth?.restorative?.caries?.present) return null;
    const caries = tooth.restorative.caries;
    const severityMap: Record<string, string> = {
      incipient: 'I',
      moderate: 'M',
      severe: 'S',
    };
    return {
      surfaces: caries.surfaces || [],
      severity: caries.severity ? severityMap[caries.severity] || '' : '',
    };
  };

  const hasRootCanal = (tooth: ToothData | undefined) => {
    return tooth?.restorative?.root_canal?.treated || false;
  };

  const getTreatmentNeeded = (tooth: ToothData | undefined) => {
    if (!tooth?.restorative?.treatment_needed?.procedure) return null;
    const treatment = tooth.restorative.treatment_needed;
    const priorityMap: Record<string, { symbol: string; color: string }> = {
      urgent: { symbol: '!!!', color: 'text-red-600' },
      soon: { symbol: '!!', color: 'text-orange-600' },
      elective: { symbol: '!', color: 'text-green-600' },
    };
    return {
      procedure: treatment.procedure,
      priority: treatment.priority ? priorityMap[treatment.priority] : null,
    };
  };

  const renderArchTable = (toothRange: number[], archName: string, isUpper: boolean) => {
    return (
      <div className="mb-8">
        <h3 className="text-md font-semibold text-center text-blue-600 mb-3 py-2 bg-blue-50 rounded">
          {archName}
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse border border-gray-300 text-xs">
            <thead>
              {/* Tooth Image Row */}
              <tr className="bg-gray-50">
                <th className="border border-gray-300 px-2 py-2 text-left bg-gray-200 font-bold text-[10px] w-24">
                  Tooth
                </th>
                {toothRange.map((toothNum) => (
                  <th key={toothNum} className="border border-gray-300 px-1 py-1 text-center min-w-[40px]">
                    <img
                      src={TOOTH_IMAGES[toothNum]}
                      alt={`Tooth ${toothNum}`}
                      className="w-7 h-7 mx-auto object-contain"
                    />
                  </th>
                ))}
              </tr>
              {/* Tooth Number Row */}
              <tr className="bg-gray-50">
                <th className="border border-gray-300 px-2 py-2 text-left bg-gray-200 font-bold text-[10px] w-24">
                  Tooth #
                </th>
                {toothRange.map((toothNum) => (
                  <th
                    key={toothNum}
                    className="border border-gray-300 px-1 py-2 text-center font-bold min-w-[40px]"
                  >
                    {toothNum}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {/* Status Row */}
              <tr>
                <td className="border border-gray-300 px-2 py-2 font-bold bg-gray-200 text-[10px]">
                  Status
                </td>
                {toothRange.map((toothNum) => {
                  const tooth = getToothData(toothNum);
                  return (
                    <td key={toothNum} className="border border-gray-300 px-1 py-2 text-center text-[10px]">
                      {!tooth || tooth.status === 'present' ? (
                        <span className="text-green-600">✓</span>
                      ) : tooth.status === 'missing' ? (
                        <span className="text-red-600 font-bold">✗ MIS</span>
                      ) : tooth.status === 'implant' ? (
                        <span className="text-blue-600 font-bold">⚕ IMP</span>
                      ) : (
                        <span>{tooth.status.substring(0, 3).toUpperCase()}</span>
                      )}
                    </td>
                  );
                })}
              </tr>

              {/* Mobility Row */}
              <tr className="bg-gray-50">
                <td className="border border-gray-300 px-2 py-2 font-bold bg-gray-200 text-[10px]">
                  Mobility
                </td>
                {toothRange.map((toothNum) => {
                  const tooth = getToothData(toothNum);
                  return (
                    <td key={toothNum} className="border border-gray-300 px-1 py-2 text-center text-[10px]">
                      {tooth?.mobility && tooth.mobility > 0 ? (
                        <span className="text-orange-600 font-bold">M{tooth.mobility}</span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                  );
                })}
              </tr>

              {/* Furcation Row */}
              <tr>
                <td className="border border-gray-300 px-2 py-2 font-bold bg-gray-200 text-[10px]">
                  Furcation
                </td>
                {toothRange.map((toothNum) => {
                  const tooth = getToothData(toothNum);
                  return (
                    <td key={toothNum} className="border border-gray-300 px-1 py-2 text-center text-[10px]">
                      {tooth?.furcation && tooth.furcation > 0 ? (
                        <span className="text-red-600 font-bold">F{tooth.furcation}</span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                  );
                })}
              </tr>

              {/* PD Buccal/Facial Row (for upper) or PD Lingual (for lower, shown first) */}
              <tr className="bg-gray-50">
                <td className="border border-gray-300 px-2 py-2 font-bold bg-gray-200 text-[10px]">
                  {isUpper ? 'PD Buccal (mm)' : 'PD Lingual (mm)'}
                </td>
                {toothRange.map((toothNum) => {
                  const tooth = getToothData(toothNum);
                  const sites = isUpper
                    ? getSiteMeasurements(tooth, ['MB', 'B', 'DB'])
                    : getSiteMeasurements(tooth, ['ML', 'L', 'DL']);
                  return (
                    <td key={toothNum} className="border border-gray-300 px-1 py-1 text-center text-[9px]">
                      {sites.length > 0 ? (
                        <span className="font-medium">{sites.join(' ')}</span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                  );
                })}
              </tr>

              {/* PD Lingual/Palatal Row (for upper) or PD Buccal (for lower, shown second) */}
              <tr>
                <td className="border border-gray-300 px-2 py-2 font-bold bg-gray-200 text-[10px]">
                  {isUpper ? 'PD Lingual (mm)' : 'PD Buccal (mm)'}
                </td>
                {toothRange.map((toothNum) => {
                  const tooth = getToothData(toothNum);
                  const sites = isUpper
                    ? getSiteMeasurements(tooth, ['ML', 'L', 'DL'])
                    : getSiteMeasurements(tooth, ['MB', 'B', 'DB']);
                  return (
                    <td key={toothNum} className="border border-gray-300 px-1 py-1 text-center text-[9px]">
                      {sites.length > 0 ? (
                        <span className="font-medium">{sites.join(' ')}</span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                  );
                })}
              </tr>

              {/* BOP Row */}
              <tr className="bg-gray-50">
                <td className="border border-gray-300 px-2 py-2 font-bold bg-gray-200 text-[10px]">BOP</td>
                {toothRange.map((toothNum) => {
                  const tooth = getToothData(toothNum);
                  const hasBOP = hasBleedingOnProbing(tooth);
                  return (
                    <td key={toothNum} className="border border-gray-300 px-1 py-2 text-center">
                      {hasBOP ? (
                        <span className="text-red-600 font-bold">●</span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                  );
                })}
              </tr>

              {/* Crown Row */}
              <tr>
                <td className="border border-gray-300 px-2 py-2 font-bold bg-gray-200 text-[10px]">Crown</td>
                {toothRange.map((toothNum) => {
                  const tooth = getToothData(toothNum);
                  const crown = getCrownInfo(tooth);
                  return (
                    <td key={toothNum} className="border border-gray-300 px-1 py-1 text-center text-[9px]">
                      {crown ? (
                        <div className="flex flex-col">
                          <span className="text-purple-600 font-bold">CR</span>
                          {crown.material && (
                            <span className="text-purple-800 text-[8px]">{crown.material}</span>
                          )}
                          {crown.condition && (
                            <span className="text-purple-800 text-[8px]">{crown.condition}</span>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                  );
                })}
              </tr>

              {/* Fillings Row */}
              <tr className="bg-gray-50">
                <td className="border border-gray-300 px-2 py-2 font-bold bg-gray-200 text-[10px]">Fillings</td>
                {toothRange.map((toothNum) => {
                  const tooth = getToothData(toothNum);
                  const fillings = getFillings(tooth);
                  return (
                    <td key={toothNum} className="border border-gray-300 px-1 py-1 text-center text-[9px]">
                      {fillings.length > 0 ? (
                        <span className="text-cyan-600 font-bold">{fillings.join('')}</span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                  );
                })}
              </tr>

              {/* Caries Row */}
              <tr>
                <td className="border border-gray-300 px-2 py-2 font-bold bg-gray-200 text-[10px]">Caries</td>
                {toothRange.map((toothNum) => {
                  const tooth = getToothData(toothNum);
                  const caries = getCaries(tooth);
                  return (
                    <td key={toothNum} className="border border-gray-300 px-1 py-1 text-center text-[9px]">
                      {caries ? (
                        <div className="flex flex-col">
                          <span className="text-red-600 font-bold">●</span>
                          {caries.surfaces.length > 0 && (
                            <span className="text-red-700 text-[8px]">{caries.surfaces.join('')}</span>
                          )}
                          {caries.severity && (
                            <span className="text-red-700 text-[8px]">{caries.severity}</span>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                  );
                })}
              </tr>

              {/* RCT Row */}
              <tr className="bg-gray-50">
                <td className="border border-gray-300 px-2 py-2 font-bold bg-gray-200 text-[10px]">RCT</td>
                {toothRange.map((toothNum) => {
                  const tooth = getToothData(toothNum);
                  const hasRCT = hasRootCanal(tooth);
                  return (
                    <td key={toothNum} className="border border-gray-300 px-1 py-2 text-center">
                      {hasRCT ? (
                        <span className="text-green-600 font-bold">✓</span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                  );
                })}
              </tr>

              {/* Treatment Needed Row */}
              <tr>
                <td className="border border-gray-300 px-2 py-2 font-bold bg-gray-200 text-[10px]">Tx Needed</td>
                {toothRange.map((toothNum) => {
                  const tooth = getToothData(toothNum);
                  const treatment = getTreatmentNeeded(tooth);
                  return (
                    <td key={toothNum} className="border border-gray-300 px-1 py-1 text-center text-[9px]">
                      {treatment ? (
                        <div className="flex flex-col">
                          {treatment.priority && (
                            <span className={`${treatment.priority.color} font-bold text-[10px]`}>
                              {treatment.priority.symbol}
                            </span>
                          )}
                          <span className={`${treatment.priority?.color || 'text-gray-700'} text-[8px] truncate`}>
                            {treatment.procedure.substring(0, 10)}
                          </span>
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                  );
                })}
              </tr>
            </tbody>
          </table>
        </div>
        <div className="mt-3 text-xs text-gray-600 bg-gray-50 p-3 rounded">
          <p>
            <strong>Legend:</strong> ✓ = Present | ✗ MIS = Missing | ⚕ IMP = Implant | M# = Mobility | F# =
            Furcation | ● = Bleeding/Caries
          </p>
          <p className="mt-1">
            <strong>Restorative:</strong> CR = Crown | RCT = Root Canal Treated | Tx Needed = Treatment Needed
          </p>
          <p className="mt-1">
            <strong>Crown Materials:</strong> PFM = Porcelain-Fused-Metal | CER = Ceramic | ZIR = Zirconia | AU
            = Gold | EMX = E-max | SS = Stainless Steel
          </p>
          <p className="mt-1">
            <strong>Condition:</strong> G = Good | F = Fair | P = Poor
          </p>
          <p className="mt-1">
            <strong>Caries Severity:</strong> I = Incipient | M = Moderate | S = Severe
          </p>
          <p className="mt-1">
            <strong>Treatment Priority:</strong> <span className="text-red-600">!!! = Urgent</span> | <span className="text-orange-600">!! = Soon</span> | <span className="text-green-600">! = Elective</span>
          </p>
        </div>
      </div>
    );
  };

  // Upper arch: 1-16
  const upperTeeth = Array.from({ length: 16 }, (_, i) => i + 1);
  // Lower arch: 32-17 (reversed)
  const lowerTeeth = Array.from({ length: 16 }, (_, i) => 32 - i);

  return (
    <div>
      {renderArchTable(upperTeeth, 'UPPER ARCH (MAXILLARY)', true)}
      {renderArchTable(lowerTeeth, 'LOWER ARCH (MANDIBULAR)', false)}
    </div>
  );
}

// Detailed Tooth Summary Component with Accordion Design
function DetailedToothSummary({ teeth }: { teeth: ToothData[] }) {
  const [expandedTooth, setExpandedTooth] = React.useState<number | null>(null);
  
  // Sort teeth by tooth number
  const sortedTeeth = [...teeth].sort((a, b) => a.tooth_number - b.tooth_number);

  const formatMaterial = (material: string | undefined) => {
    if (!material) return 'N/A';
    const materialMap: Record<string, string> = {
      pfm: 'Porcelain-Fused-Metal',
      ceramic: 'Ceramic',
      zirconia: 'Zirconia',
      gold: 'Gold',
      emax: 'E-max',
      stainless_steel: 'Stainless Steel',
      amalgam: 'Amalgam',
      composite: 'Composite',
      glass_ionomer: 'Glass Ionomer',
      porcelain: 'Porcelain',
      other: 'Other',
    };
    return materialMap[material] || material;
  };

  const formatCondition = (condition: string | undefined) => {
    const conditionMap: Record<string, { label: string; color: string }> = {
      good: { label: 'Good', color: 'text-green-600' },
      fair: { label: 'Fair', color: 'text-orange-600' },
      poor: { label: 'Poor', color: 'text-red-600' },
    };
    if (!condition) return { label: 'N/A', color: 'text-gray-600' };
    return conditionMap[condition] || { label: condition, color: 'text-gray-600' };
  };

  const hasRestorativeWork = (tooth: ToothData) => {
    return (
      tooth.restorative?.crown?.present ||
      (tooth.restorative?.fillings && tooth.restorative.fillings.length > 0) ||
      tooth.restorative?.root_canal?.treated ||
      tooth.restorative?.veneer?.present ||
      tooth.restorative?.caries?.present ||
      tooth.restorative?.fracture?.present ||
      tooth.restorative?.treatment_needed
    );
  };

  const hasPeriodontalFindings = (tooth: ToothData) => {
    return (
      (tooth.mobility && tooth.mobility > 0) ||
      (tooth.furcation && tooth.furcation > 0) ||
      (tooth.sites && tooth.sites.some(s => (s.pocket_depth && s.pocket_depth >= 4) || s.bleeding))
    );
  };

  const getToothSummaryBadges = (tooth: ToothData) => {
    const badges = [];
    if (tooth.restorative?.crown?.present) badges.push({ label: 'Crown', color: 'bg-purple-100 text-purple-700' });
    if (tooth.restorative?.fillings && tooth.restorative.fillings.length > 0) {
      badges.push({ label: `${tooth.restorative.fillings.length} Filling${tooth.restorative.fillings.length > 1 ? 's' : ''}`, color: 'bg-blue-100 text-blue-700' });
    }
    if (tooth.restorative?.root_canal?.treated) badges.push({ label: 'RCT', color: 'bg-green-100 text-green-700' });
    if (tooth.restorative?.veneer?.present) badges.push({ label: 'Veneer', color: 'bg-indigo-100 text-indigo-700' });
    if (tooth.restorative?.caries?.present) badges.push({ label: 'Caries', color: 'bg-red-100 text-red-700' });
    if (tooth.restorative?.fracture?.present) badges.push({ label: 'Fracture', color: 'bg-orange-100 text-orange-700' });
    if (tooth.mobility && tooth.mobility > 0) badges.push({ label: `Mob ${tooth.mobility}`, color: 'bg-yellow-100 text-yellow-700' });
    if (tooth.furcation && tooth.furcation > 0) badges.push({ label: `Furc ${tooth.furcation}`, color: 'bg-red-100 text-red-700' });
    return badges;
  };

  // Filter teeth with data
  const teethWithData = sortedTeeth.filter(
    (tooth) => tooth.status !== 'missing' && (hasRestorativeWork(tooth) || hasPeriodontalFindings(tooth) || tooth.notes)
  );

  if (teethWithData.length === 0) {
    return (
      <div className="text-center py-12 bg-gray-50 rounded-lg">
        <FileText className="w-12 h-12 mx-auto text-gray-400 mb-3" />
        <p className="text-gray-500 text-lg">No detailed tooth information recorded.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {teethWithData.map((tooth) => {
        const isExpanded = expandedTooth === tooth.tooth_number;
        const badges = getToothSummaryBadges(tooth);
        
        return (
          <div
            key={tooth.tooth_number}
            className="border border-gray-300 rounded-lg overflow-hidden bg-white shadow-sm hover:shadow-md transition-all"
          >
            {/* Tooth Header - Always Visible */}
            <button
              onClick={() => setExpandedTooth(isExpanded ? null : tooth.tooth_number)}
              className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-4">
                <img
                  src={TOOTH_IMAGES[tooth.tooth_number]}
                  alt={`Tooth ${tooth.tooth_number}`}
                  className="w-16 h-16 object-contain"
                />
                <div className="text-left">
                  <h3 className="text-xl font-bold text-gray-900">Tooth #{tooth.tooth_number}</h3>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-sm text-gray-600 capitalize">
                      {tooth.status === 'present' ? 'Present' : tooth.status}
                    </span>
                    {badges.length > 0 && (
                      <span className="text-gray-400">•</span>
                    )}
                    <div className="flex flex-wrap gap-1">
                      {badges.slice(0, 3).map((badge, idx) => (
                        <span
                          key={idx}
                          className={`px-2 py-0.5 rounded text-xs font-medium ${badge.color}`}
                        >
                          {badge.label}
                        </span>
                      ))}
                      {badges.length > 3 && (
                        <span className="px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                          +{badges.length - 3} more
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <ChevronDown 
                className={`w-5 h-5 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
              />
            </button>

            {/* Expanded Content */}
            {isExpanded && (
              <div className="px-6 pb-6 pt-2 border-t border-gray-200 bg-gray-50">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Left Column - Periodontal Findings */}
                  {hasPeriodontalFindings(tooth) && (
                    <div>
                      <h4 className="text-sm font-bold text-gray-900 mb-3 flex items-center uppercase tracking-wide">
                        <Activity className="w-4 h-4 mr-2 text-blue-600" />
                        Periodontal Findings
                      </h4>
                      <div className="space-y-3 bg-white rounded-lg p-4 border border-gray-200">
                        {tooth.mobility && tooth.mobility > 0 && (
                          <div>
                            <span className="text-xs font-semibold text-gray-500 uppercase">Mobility</span>
                            <p className="text-sm text-gray-900 mt-1">
                              Grade <span className="font-bold text-orange-600">{tooth.mobility}</span>
                            </p>
                          </div>
                        )}
                        {tooth.furcation && tooth.furcation > 0 && (
                          <div>
                            <span className="text-xs font-semibold text-gray-500 uppercase">Furcation</span>
                            <p className="text-sm text-gray-900 mt-1">
                              Grade <span className="font-bold text-red-600">{tooth.furcation}</span>
                              {tooth.furcation_locations && tooth.furcation_locations.length > 0 && (
                                <span className="text-gray-600 ml-1">({tooth.furcation_locations.join(', ')})</span>
                              )}
                            </p>
                          </div>
                        )}
                        {tooth.sites && tooth.sites.some(s => s.pocket_depth && s.pocket_depth >= 4) && (
                          <div>
                            <span className="text-xs font-semibold text-gray-500 uppercase">Deep Pockets (≥4mm)</span>
                            <p className="text-sm text-gray-900 mt-1">
                              {tooth.sites
                                .filter(s => s.pocket_depth && s.pocket_depth >= 4)
                                .map(s => `${s.site} (${s.pocket_depth}mm)`)
                                .join(', ')}
                            </p>
                          </div>
                        )}
                        {tooth.sites && tooth.sites.some(s => s.bleeding) && (
                          <div>
                            <span className="text-xs font-semibold text-gray-500 uppercase">Bleeding on Probing</span>
                            <p className="text-sm text-red-600 mt-1 font-medium">
                              {tooth.sites.filter(s => s.bleeding).map(s => s.site).join(', ')}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Right Column - Restorative Information */}
                  {hasRestorativeWork(tooth) && (
                    <div>
                      <h4 className="text-sm font-bold text-gray-900 mb-3 flex items-center uppercase tracking-wide">
                        <FileText className="w-4 h-4 mr-2 text-purple-600" />
                        Restorative Information
                      </h4>
                      <div className="space-y-3">
                        {/* Crown */}
                        {tooth.restorative?.crown?.present && (
                          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                            <div className="flex items-center gap-2 mb-2">
                              <div className="w-2 h-2 rounded-full bg-purple-600"></div>
                              <span className="font-bold text-purple-900 text-sm uppercase tracking-wide">Crown</span>
                            </div>
                            <dl className="space-y-1.5 text-sm">
                              <div className="flex justify-between">
                                <dt className="text-gray-600">Material:</dt>
                                <dd className="font-medium text-gray-900">{formatMaterial(tooth.restorative.crown.material)}</dd>
                              </div>
                              {tooth.restorative.crown.condition && (
                                <div className="flex justify-between">
                                  <dt className="text-gray-600">Condition:</dt>
                                  <dd className={`font-semibold ${formatCondition(tooth.restorative.crown.condition).color}`}>
                                    {formatCondition(tooth.restorative.crown.condition).label}
                                  </dd>
                                </div>
                              )}
                            </dl>
                          </div>
                        )}

                        {/* Fillings */}
                        {tooth.restorative?.fillings && tooth.restorative.fillings.length > 0 && (
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                            <div className="flex items-center gap-2 mb-2">
                              <div className="w-2 h-2 rounded-full bg-blue-600"></div>
                              <span className="font-bold text-blue-900 text-sm uppercase tracking-wide">
                                Filling{tooth.restorative.fillings.length > 1 ? 's' : ''}
                              </span>
                            </div>
                            {tooth.restorative.fillings.map((filling, idx, arr) => (
                              <dl key={idx} className="space-y-1.5 text-sm mb-3 last:mb-0">
                                <div className="flex justify-between">
                                  <dt className="text-gray-600">Surfaces:</dt>
                                  <dd className="font-medium text-gray-900">{filling.surfaces.join(', ')}</dd>
                                </div>
                                <div className="flex justify-between">
                                  <dt className="text-gray-600">Material:</dt>
                                  <dd className="font-medium text-gray-900">{formatMaterial(filling.material)}</dd>
                                </div>
                                {filling.condition && (
                                  <div className="flex justify-between">
                                    <dt className="text-gray-600">Condition:</dt>
                                    <dd className={`font-semibold ${formatCondition(filling.condition).color}`}>
                                      {formatCondition(filling.condition).label}
                                    </dd>
                                  </div>
                                )}
                                {idx < arr.length - 1 && (
                                  <div className="border-t border-blue-200 pt-2"></div>
                                )}
                              </dl>
                            ))}
                          </div>
                        )}

                        {/* Root Canal */}
                        {tooth.restorative?.root_canal?.treated && (
                          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                            <div className="flex items-center gap-2 mb-2">
                              <div className="w-2 h-2 rounded-full bg-green-600"></div>
                              <span className="font-bold text-green-900 text-sm uppercase tracking-wide">Root Canal Treatment</span>
                            </div>
                            <dl className="space-y-1.5 text-sm">
                              <div className="flex justify-between">
                                <dt className="text-gray-600">Status:</dt>
                                <dd className="font-medium text-green-700">Treated</dd>
                              </div>
                              {tooth.restorative.root_canal.has_post && (
                                <div className="flex justify-between">
                                  <dt className="text-gray-600">Post:</dt>
                                  <dd className="font-medium text-gray-900">Present</dd>
                                </div>
                              )}
                            </dl>
                          </div>
                        )}

                        {/* Veneer */}
                        {tooth.restorative?.veneer?.present && (
                          <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                            <div className="flex items-center gap-2 mb-2">
                              <div className="w-2 h-2 rounded-full bg-indigo-600"></div>
                              <span className="font-bold text-indigo-900 text-sm uppercase tracking-wide">Veneer</span>
                            </div>
                            <dl className="space-y-1.5 text-sm">
                              {tooth.restorative.veneer.material && (
                                <div className="flex justify-between">
                                  <dt className="text-gray-600">Material:</dt>
                                  <dd className="font-medium text-gray-900">{formatMaterial(tooth.restorative.veneer.material)}</dd>
                                </div>
                              )}
                              {tooth.restorative.veneer.condition && (
                                <div className="flex justify-between">
                                  <dt className="text-gray-600">Condition:</dt>
                                  <dd className={`font-semibold ${formatCondition(tooth.restorative.veneer.condition).color}`}>
                                    {formatCondition(tooth.restorative.veneer.condition).label}
                                  </dd>
                                </div>
                              )}
                            </dl>
                          </div>
                        )}

                        {/* Caries */}
                        {tooth.restorative?.caries?.present && (
                          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                            <div className="flex items-center gap-2 mb-2">
                              <div className="w-2 h-2 rounded-full bg-red-600"></div>
                              <span className="font-bold text-red-900 text-sm uppercase tracking-wide">Caries/Decay</span>
                            </div>
                            <dl className="space-y-1.5 text-sm">
                              {tooth.restorative.caries.surfaces && tooth.restorative.caries.surfaces.length > 0 && (
                                <div className="flex justify-between">
                                  <dt className="text-gray-600">Surfaces:</dt>
                                  <dd className="font-medium text-gray-900">{tooth.restorative.caries.surfaces.join(', ')}</dd>
                                </div>
                              )}
                              {tooth.restorative.caries.severity && (
                                <div className="flex justify-between">
                                  <dt className="text-gray-600">Severity:</dt>
                                  <dd className={`font-semibold ${
                                    tooth.restorative.caries.severity === 'severe' ? 'text-red-600' :
                                    tooth.restorative.caries.severity === 'moderate' ? 'text-orange-600' :
                                    'text-yellow-600'
                                  }`}>
                                    {tooth.restorative.caries.severity.charAt(0).toUpperCase() + tooth.restorative.caries.severity.slice(1)}
                                  </dd>
                                </div>
                              )}
                            </dl>
                          </div>
                        )}

                        {/* Fracture */}
                        {tooth.restorative?.fracture?.present && (
                          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                            <div className="flex items-center gap-2 mb-2">
                              <div className="w-2 h-2 rounded-full bg-orange-600"></div>
                              <span className="font-bold text-orange-900 text-sm uppercase tracking-wide">Fracture</span>
                            </div>
                            <dl className="space-y-1.5 text-sm">
                              {tooth.restorative.fracture.type && (
                                <div className="flex justify-between">
                                  <dt className="text-gray-600">Type:</dt>
                                  <dd className="font-medium text-gray-900 capitalize">{tooth.restorative.fracture.type.replace('_', ' ')}</dd>
                                </div>
                              )}
                              {tooth.restorative.fracture.location && (
                                <div className="flex justify-between">
                                  <dt className="text-gray-600">Location:</dt>
                                  <dd className="font-medium text-gray-900">{tooth.restorative.fracture.location}</dd>
                                </div>
                              )}
                            </dl>
                          </div>
                        )}

                        {/* Treatment Needed */}
                        {tooth.restorative?.treatment_needed && (
                          <div className={`border rounded-lg p-4 ${
                            tooth.restorative.treatment_needed.priority === 'urgent' ? 'bg-red-50 border-red-300' :
                            tooth.restorative.treatment_needed.priority === 'soon' ? 'bg-orange-50 border-orange-300' :
                            'bg-blue-50 border-blue-300'
                          }`}>
                            <div className="flex items-center gap-2 mb-2">
                              <AlertCircle className={`w-4 h-4 ${
                                tooth.restorative.treatment_needed.priority === 'urgent' ? 'text-red-600' :
                                tooth.restorative.treatment_needed.priority === 'soon' ? 'text-orange-600' :
                                'text-blue-600'
                              }`} />
                              <span className={`font-bold text-sm uppercase tracking-wide ${
                                tooth.restorative.treatment_needed.priority === 'urgent' ? 'text-red-900' :
                                tooth.restorative.treatment_needed.priority === 'soon' ? 'text-orange-900' :
                                'text-blue-900'
                              }`}>
                                Treatment Needed
                              </span>
                              {tooth.restorative.treatment_needed.priority && (
                                <span className={`ml-auto px-2 py-0.5 rounded text-xs font-bold ${
                                  tooth.restorative.treatment_needed.priority === 'urgent' ? 'bg-red-200 text-red-900' :
                                  tooth.restorative.treatment_needed.priority === 'soon' ? 'bg-orange-200 text-orange-900' :
                                  'bg-blue-200 text-blue-900'
                                }`}>
                                  {tooth.restorative.treatment_needed.priority.toUpperCase()}
                                </span>
                              )}
                            </div>
                            <dl className="space-y-1.5 text-sm">
                              <div className="flex justify-between">
                                <dt className="text-gray-600">Procedure:</dt>
                                <dd className="font-medium text-gray-900 uppercase">{tooth.restorative.treatment_needed.procedure.replace('_', ' ')}</dd>
                              </div>
                              {tooth.restorative.treatment_needed.notes && (
                                <div className="mt-2 pt-2 border-t border-gray-300">
                                  <dt className="text-gray-600 text-xs font-semibold uppercase mb-1">Notes:</dt>
                                  <dd className="text-gray-900">{tooth.restorative.treatment_needed.notes}</dd>
                                </div>
                              )}
                            </dl>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Tooth Notes - Full Width */}
                {tooth.notes && (
                  <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <h5 className="text-xs font-bold text-yellow-900 uppercase tracking-wide mb-2 flex items-center">
                      <FileText className="w-3 h-3 mr-1" />
                      Clinical Notes
                    </h5>
                    <p className="text-sm text-gray-900 whitespace-pre-wrap">{tooth.notes}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
